import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  MenuItem,
} from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { AuthContext } from '../../../authContext/context';
import { postRequest } from '../../../lib/api';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';


const CreateAppointment = () => {
  const authCtx = useContext(AuthContext);
  //const [searchParams] = useSearchParams();
  const {id} = useParams();
  const  navigate = useNavigate()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const [showCalendar, setShowCalendar] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [solicitorList, setSolicitorList] = useState([]);

  const solicitorId = watch('solicitor');
  const calendarRef = useRef(null);

  const fetchSolicitors = async () => {
    try {
      const res = await postRequest('private/getSolicitorsData', {}, authCtx.token);
      if (res?.status === 200) {
        setSolicitorList(res.data || []);
      } else {
        toast.error('Failed to fetch solicitors');
      }
    } catch (err) {
      toast.error('Error fetching solicitors');
    }
  };

  const fetchAppointments = async (id) => {
    setLoading(true);
    try {
      const res = await postRequest(`private/viewAvaliablitySolicitor/${id}`, {}, authCtx.token);
      if (res?.status === 200 && Array.isArray(res.data)) {
        const formatted = res.data.map((slot) => ({
          title: slot.title || 'Booked',
          date: slot.date, // e.g. '2025-07-10T11:00:00'
        }));
        setAppointments(formatted);
      } else {
        toast.error(res?.message || 'No availability found');
      }
    } catch (err) {
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    const payload = {
      customer_id: id,
      solicitor_id: data.solicitor,
      appointment_datetime: data.date,
      ti_case_ref: data.ti_case_ref,
      note: data.note,
      status:'Active',
      created_id: localStorage.getItem('id') || '',
    };

    try {
      const response = await postRequest('private/createAppointment', payload, authCtx.token);
      if (response?.status === 200) {
        toast.success('Appointment created successfully');
         navigate(-1)
      } else {
        toast.error(response?.message || 'Error creating appointment');
      }
    } catch (err) {
      toast.error(err?.message || 'Something went wrong');
    }
  };

  useEffect(() => {
    fetchSolicitors();
  }, []);

  return (
    <Box sx={{ p: 3, background: '#fff', borderRadius: 2 }}>
      <Typography variant="h5" mb={3}>
        Create Appointment
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          {/* 👩 Solicitor Dropdown */}
          <TextField
            select
            label="Select Solicitor"
            {...register('solicitor', { required: 'Solicitor is required' })}
            error={!!errors.solicitor}
            helperText={errors.solicitor?.message}
          >
            {solicitorList.map((sol) => (
              <MenuItem key={sol._id} value={sol._id}>
                {sol.forename} {sol.surname}
              </MenuItem>
            ))}
          </TextField>

          {/* 📆 Date/Time Field */}
          <TextField
            type="datetime-local"
            label="Appointment Date/Time"
            InputLabelProps={{ shrink: true }}
            {...register('date', { required: 'Date is required' })}
            error={!!errors.date}
            helperText={errors.date?.message}
          />

          {/* 📅 View Calendar */}
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              if (!solicitorId) {
                toast.error('Please select a solicitor first');
              } else {
                fetchAppointments(solicitorId);
                setShowCalendar(true);
              }
            }}
          >
            View Solicitor Availability
          </Button>

          {/* 📆 Full Calendar */}
          {showCalendar && (
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              events={appointments}
              height={450}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: '',
              }}
              dateClick={(arg) => {
                const clickedDate = dayjs(arg.date).format('YYYY-MM-DD');
                const currentTime = dayjs().format('HH:mm');
                const fullDateTime = `${clickedDate}T${currentTime}`;
                setValue('date', fullDateTime);
                calendarRef.current?.getApi().gotoDate(clickedDate);
                //toast.success(`Date selected: ${clickedDate}`);
              }}
              eventContent={(eventInfo) => (
                <div
                  style={{
                    backgroundColor: '#d32f2f',
                    color: 'white',
                    padding: '4px',
                    borderRadius: 4,
                    fontSize: '13px',
                  }}
                >
                  {eventInfo.event.title}
                </div>
              )}
              dayCellContent={(arg) => (
                <div style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                  {arg.dayNumberText}
                </div>
              )}
            />
          )}

          {/* Optional Fields */}
          <TextField label="TI Case Ref" {...register('ti_case_ref')} />
          <TextField label="Note" multiline rows={4} {...register('note')} />

          <Stack direction="row" spacing={2}>
            <Button type="submit" className='red-bg red-button text-capitalize' variant="contained" s>
              Submit
            </Button>
            <Button type="button"  color="inherit" variant="outlined" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default CreateAppointment;
